import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Card, Table, message, InputNumber, Divider, Typography, Row, Col, Grid } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const MillingForm = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [form] = Form.useForm();
    const [itemForm] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Dropdown Data
    const [branches, setBranches] = useState([]);
    const [palayLots, setPalayLots] = useState([]);

    // Dynamic Items State
    const [items, setItems] = useState([]);
    const selectedLotId = Form.useWatch('palay_lot_id', itemForm);
    const selectedLot = palayLots.find(l => l.id === selectedLotId);

    useEffect(() => {
        fetchMasterData();
    }, []);

    const fetchMasterData = async () => {
        try {
            const [branchRes, lotRes] = await Promise.all([
                client.get('/master/branches'),
                client.get('/inventory/palay-lots')
            ]);
            setBranches(branchRes.data);
            setPalayLots(lotRes.data || []);
        } catch (error) {
            message.error('Failed to load master data');
        }
    };

    const handleAddItem = (values) => {
        if (items.some(i => i.palay_lot_id === values.palay_lot_id)) {
            message.warning('This lot has already been added to the order.');
            return;
        }
        const lot = palayLots.find(l => l.id === values.palay_lot_id);
        if (lot && Number(values.quantity) > Number(lot.current_quantity || 0)) {
            message.error(`Quantity exceeds the ${Number(lot.current_quantity).toLocaleString()} kg available in this lot.`);
            return;
        }
        const locationLabel = lot?.storage_code;
        const newItem = {
            ...values,
            key: Date.now(),
            palay_lot_id: values.palay_lot_id,
            item_name: lot
                ? `${lot.lot_number} - ${lot.item?.name || 'Palay Lot'}${locationLabel ? ` (${locationLabel})` : ''}`
                : 'Unknown Lot',
            quantity: values.quantity,
        };
        setItems([...items, newItem]);
        itemForm.resetFields();
    };

    const handleDeleteItem = (key) => {
        setItems(items.filter(item => item.key !== key));
    };

    const onFinish = async (values) => {
        if (items.length === 0) {
            message.error('Please add at least one input item.');
            return;
        }

        setLoading(true);
        try {
            const orderNumber = values.order_number?.trim();
            const payload = {
                ...values,
                order_number: orderNumber || `MO-${dayjs().format('YYYYMMDD-HHmmss')}`,
                milling_date: values.milling_date.format('YYYY-MM-DD'),
                total_input_quantity: items.reduce((sum, item) => sum + item.quantity, 0),
                items: items.map(item => ({
                    palay_lot_id: item.palay_lot_id,
                    quantity: item.quantity
                }))
            };

            await client.post('/transactions/milling', payload);
            message.success('Milling Order created successfully');
            navigate('/milling');
        } catch (error) {
            message.error('Failed to create order. ' + (error.response?.data?.message || ''));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Input Material / Lot',
            dataIndex: 'item_name',
            key: 'item_name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Quantity (kg)',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (q) => <Typography.Text strong>{q.toLocaleString()} kg</Typography.Text>
        },
        {
            title: 'Action',
            key: 'action',
            align: 'right',
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteItem(record.key)}
                    style={{ borderRadius: 8 }}
                />
            )
        }
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/milling')}
                        style={{ marginBottom: 12, borderRadius: 8 }}
                    >
                        Back to Milling List
                    </Button>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Create Milling Order</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Initiate production process and track material recovery.</Typography.Text>
                </div>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={[24, 24]}>
                    <Col lg={16}>
                        <Card className="premium-card" title={<Typography.Text strong>Production Configuration</Typography.Text>}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="order_number" label={<Typography.Text strong>Milling Order # (Optional)</Typography.Text>}>
                                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="MO-AUTO" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="milling_date" label={<Typography.Text strong>Processing Date</Typography.Text>} rules={[{ required: true }]}>
                                        <DatePicker style={{ width: '100%', height: 40, borderRadius: 8 }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="branch_id" label={<Typography.Text strong>Processing Branch</Typography.Text>} rules={[{ required: true }]}>
                                        <Select placeholder="Select Facility" style={{ height: 40 }}>
                                            {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="mill_operator" label={<Typography.Text strong>Mill Operator / Supervisor</Typography.Text>} rules={[{ required: true }]}>
                                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="Enter operator name" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider style={{ margin: '8px 0 16px' }} />

                            <Card
                                type="inner"
                                title={<Typography.Text strong>Input Raw Materials</Typography.Text>}
                                bodyStyle={{ padding: 16 }}
                                style={{ borderRadius: 12 }}
                            >
                                <Form form={itemForm} layout="vertical" onFinish={handleAddItem}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 12 }}>
                                        <Form.Item name="palay_lot_id" label={<Typography.Text strong>Select Palay Lot</Typography.Text>} style={{ flex: 2, marginBottom: 0 }} rules={[{ required: true }]}>
                                            <Select placeholder="Choose lot for processing" style={{ height: 40 }}>
                                                {palayLots.map(lot => (
                                                    <Option key={lot.id} value={lot.id}>
                                                        {lot.lot_number} - {lot.item?.name || 'Palay Lot'}
                                                        {lot.storage_code ? ` (${lot.storage_code})` : ''}
                                                        {` - ${lot.purchase?.partner?.name || 'No supplier'}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item label={<Typography.Text strong>Remaining (kg)</Typography.Text>} style={{ flex: 1, marginBottom: 0 }}>
                                            <InputNumber
                                                value={selectedLot ? Number(selectedLot.current_quantity || 0) : 0}
                                                readOnly
                                                style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                formatter={(value) => Number(value || 0).toLocaleString()}
                                            />
                                        </Form.Item>
                                        <Form.Item name="quantity" label={<Typography.Text strong>Quantity (kg)</Typography.Text>} style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true }]}>
                                            <InputNumber
                                                min={0.01}
                                                max={selectedLot ? Number(selectedLot.current_quantity || 0) : undefined}
                                                style={{ width: '100%', height: 40, borderRadius: 8 }}
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                        <Button
                                            type="primary"
                                            onClick={() => itemForm.submit()}
                                            icon={<PlusOutlined />}
                                            style={{ height: 40, borderRadius: 8, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                                        >
                                            Add Line
                                        </Button>
                                    </div>
                                </Form>

                                <Table
                                    dataSource={items}
                                    columns={columns}
                                    pagination={false}
                                    size={isMobile ? 'small' : 'middle'}
                                    scroll={isMobile ? { x: 700 } : undefined}
                                    locale={{ emptyText: 'No raw materials added to order.' }}
                                />
                            </Card>
                        </Card>
                    </Col>

                    <Col lg={8}>
                        <Card className="premium-card" title={<Typography.Text strong>Processing Summary</Typography.Text>}>
                            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Total Input Volume</Typography.Text>
                                <Title level={2} style={{ margin: '8px 0', color: '#10b981' }}>
                                    {items.reduce((sum, i) => sum + i.quantity, 0).toLocaleString()} kg
                                </Title>
                                <Typography.Text style={{ color: 'var(--text-secondary)' }}>{items.length} lots in queue</Typography.Text>
                            </div>
                            <Divider style={{ margin: '12px 0' }} />
                            <div style={{ marginBottom: 24 }}>
                                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Processing Notes</Typography.Text>
                                <Input.TextArea placeholder="Add recovery expectations or milling instructions..." rows={4} style={{ borderRadius: 8 }} />
                            </div>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={loading}
                                block
                                size="large"
                                style={{ height: 50, borderRadius: 12, fontWeight: 700, fontSize: 16 }}
                            >
                                Start Milling Order
                            </Button>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default MillingForm;



